import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { LayoutDashboard, Server, RotateCcw, Bell, ScrollText, CreditCard, Network, LifeBuoy, Settings, Rocket, LogOut, Shield } from "lucide-react";

const nav = [
  ["/app/dashboard", LayoutDashboard, "Dashboard"],
  ["/app/deploy", Rocket, "Deploy Server"],
  ["/app/restore", RotateCcw, "Restore Center"],
  ["/app/alerts", Bell, "Alerts"],
  ["/app/wireguard", Network, "WireGuard"],
  ["/app/billing", CreditCard, "Billing"],
  ["/app/tickets", LifeBuoy, "Support"],
  ["/app/audit", ScrollText, "Audit Log"],
  ["/app/settings", Settings, "Settings"],
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#06080D] lg:flex">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-slate-800 bg-[#080B12] lg:flex">
        <div className="border-b border-slate-800 p-4"><Logo to="/app/dashboard" /></div>
        <nav className="flex-1 space-y-1 p-3">
          {nav.map(([to, Icon, label]) => (
            <NavLink key={to} to={to} data-testid={`sidebar-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-cyan-500/10 text-cyan-300" : "text-slate-400 hover:bg-slate-900 hover:text-slate-200"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
          {user?.platform_role === "admin" && (
            <NavLink to="/admin" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-amber-300/90 hover:bg-slate-900">
              <Shield className="h-4 w-4" /> Admin Portal
            </NavLink>
          )}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="mb-2 px-2 text-xs text-slate-500">{user?.company}</div>
          <div className="mb-2 truncate px-2 text-sm text-slate-300">{user?.email}</div>
          <button onClick={doLogout} data-testid="logout-btn" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-900 hover:text-rose-300">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="flex items-center justify-between border-b border-slate-800 p-3 lg:hidden">
        <Logo to="/app/dashboard" />
        <button onClick={doLogout} className="text-slate-400"><LogOut className="h-5 w-5" /></button>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-800 px-2 py-2 lg:hidden">
        {nav.map(([to, Icon, label]) => (
          <NavLink key={to} to={to} className={({ isActive }) => `flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs ${isActive ? "bg-cyan-500/10 text-cyan-300" : "text-slate-400"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </NavLink>
        ))}
      </div>

      <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}
